import { create } from "zustand";
import type { Band, Bands } from "@/lib/setup/lanes";
import type { SeedNetwork } from "@/lib/setup/preview";
import type { SeedCandidate } from "@/stores/seed-discovery-store";
import type { Recency } from "@/types/concept";

/** Cache key for anything that depends on recency (limits do). `id` is an S2 id
 *  for `networks`, a seed key for `status`. */
export const netKey = (id: string, recency: Recency | undefined) => `${id}:${recency ?? "balanced"}`;

/** A seed as setup tracks it. `key` is the card key: the S2 id when known, else
 *  `db:<row id>` (imported .bib/Zotero papers). `seedKey` derives the same key
 *  from a saved row, so the preview at ③ and Start (which reads saved rows)
 *  agree on identity. */
export type SeedRef = { key: string; paper_id: string | null; doi: string | null; title: string };
export const seedKey = (p: { id: string; paper_id: string | null }) => p.paper_id ?? `db:${p.id}`;

/** "retrying": S2 didn't answer, trying again after a pause. "empty": S2
 *  answered and the seed has no network (or no S2 record) — final. "failed":
 *  S2 still didn't answer after the retries. */
export type NetStatus = "loading" | "retrying" | "ok" | "empty" | "failed";
export const isSettled = (st: NetStatus | undefined) => st === "ok" || st === "empty" || st === "failed";

interface SetupState {
  conceptId: string | null;
  /** Start-from-a-paper pick: pre-checked at ③, its S2 fields pre-fill ②. */
  anchor: SeedCandidate | null;
  anchorFields: string[] | null;
  /** Seeds checked at ③ (drives the preview and sampling; Start re-reads saved rows). */
  checked: SeedRef[];
  /** seed key → resolved S2 id (paper_id → DOI → title). Successes only. */
  resolved: Record<string, string>;
  /** netKey(s2Id, recency) → network. */
  networks: Record<string, SeedNetwork>;
  /** netKey(seed key, recency) → fetch state. */
  status: Record<string, NetStatus>;
  /** ② quick check, per question hash (lanes.questionHash): paper key → band. */
  triage: Record<string, Bands>;
  /** Last outcome per question hash: "capped" (daily limit) stops checking; "failed" waits for Retry. */
  triageError: Record<string, "failed" | "capped" | undefined>;
  setTriage: (qh: string, sent: string[], results: { key: string; band: Band }[]) => void;
  /** ② lane preview, per question hash: suggestion key → on-question share of its
   *  most-cited citers, or null (too few, failed, or OpenAlex off: keep the selector's order). */
  lanePreview: Record<string, Record<string, number | null>>;
  setLanePreview: (qh: string, scores: Record<string, number | null>) => void;
  setTriageError: (qh: string, err: "failed" | "capped" | undefined) => void;
  begin: (conceptId: string | null) => void;
  setAnchor: (anchor: SeedCandidate | null, fields: string[] | null) => void;
  setChecked: (seeds: SeedRef[]) => void;
  setResolved: (key: string, s2Id: string) => void;
  setNetwork: (key: string, net: SeedNetwork) => void;
  setStatus: (key: string, status: NetStatus) => void;
}

const empty = { anchor: null, anchorFields: null, checked: [], resolved: {}, networks: {}, status: {}, triage: {}, triageError: {}, lanePreview: {} };

export const useSetupStore = create<SetupState>((set, get) => ({
  conceptId: null,
  ...empty,
  /** New setup (null) → clear. A fresh concept adopting this setup keeps its state;
   *  a different concept starts clean. */
  begin: (conceptId) => {
    const cur = get().conceptId;
    if (conceptId === null) set({ conceptId: null, ...empty });
    else if (cur === null) set({ conceptId });
    else if (cur !== conceptId) set({ conceptId, ...empty });
  },
  setAnchor: (anchor, anchorFields) => set({ anchor, anchorFields }),
  setChecked: (checked) => set({ checked }),
  setResolved: (key, s2Id) => set((s) => ({ resolved: { ...s.resolved, [key]: s2Id } })),
  setNetwork: (key, net) => set((s) => ({ networks: { ...s.networks, [key]: net } })),
  setStatus: (key, status) => set((s) => ({ status: { ...s.status, [key]: status } })),
  /** Every sent key is recorded: its band, or null when the model skipped it. */
  setTriage: (qh, sent, results) => set((s) => {
    const next: Bands = { ...(s.triage[qh] ?? {}) };
    for (const k of sent) next[k] = null;
    for (const r of results) next[r.key] = r.band;
    return { triage: { ...s.triage, [qh]: next } };
  }),
  setTriageError: (qh, err) => set((s) => ({ triageError: { ...s.triageError, [qh]: err } })),
  setLanePreview: (qh, scores) => set((s) => ({ lanePreview: { ...s.lanePreview, [qh]: { ...(s.lanePreview[qh] ?? {}), ...scores } } })),
}));

/** Loaded networks for these seeds (via their resolved S2 ids). Seeds still
 *  loading or failed are simply absent; callers read `status` for those. */
export function networksFor(
  state: Pick<SetupState, "resolved" | "networks">, seeds: SeedRef[], recency: Recency | undefined,
): SeedNetwork[] {
  return seeds
    .map((s) => state.resolved[s.key])
    .map((id) => (id ? state.networks[netKey(id, recency)] : undefined))
    .filter((n): n is SeedNetwork => !!n);
}
